// Banners (contracts/messaging.md §2): admin cards with an optional image on Home, Arena and Profile, listed by
// placement with their state and unique-player stats. Editors create, edit, pause and delete; every role reads.
// The app shows the first live banner of each placement (up to 3 page horizontally), highest priority first.

import { useState } from "react";
import {
  type AdminBanner,
  BANNER_AUDIENCE_LABEL,
  BANNER_AUDIENCES,
  BANNER_IMAGE,
  BANNER_PLACEMENTS,
  BANNER_STYLES,
  type BannerAudience,
  type BannerDraft,
  type BannerPlacement,
  type BannerStyle,
  PLACEMENT_LABEL,
  PRIORITY_MAX,
  PRIORITY_MIN,
  STATE_TONE,
  STYLE_LABEL,
  useBanners,
  useCreateBanner,
  useDeleteBanner,
  useUpdateBanner,
} from "../api/banners";
import { useBroadcasts } from "../api/broadcasts";
import { ACTION_LABEL, APP_LINK_LABEL, type ContentField, type ImageRef, LOCALE_LABEL, type Locale, RTL_LOCALES } from "../api/messaging";
import { useAuth, useCan } from "../auth/AuthContext";
import { tableStyles } from "../components/table";
import { Button, Card, Chip, ConfirmDialog, Dialog, EmptyState, ErrorState, Field, InlineError, Loading, PageHeader, Segmented, Select, TextInput } from "../components/ui";
import { formatDateTime } from "../lib/dates";
import { fmtInt, fmtPct } from "../lib/format";
import { fromUtcInput, MESSAGE_REQUIRED, resolveEntry, toContent, toUtcInput } from "../lib/messaging";
import b from "./banners.module.css";
import {
  ActionFields,
  actionFields,
  actionProblem,
  type ActionValue,
  ContentEditor,
  type ContentState,
  contentStateFrom,
  emptyContentState,
  type FieldSpec,
  ImageField,
  MESSAGE_FIELD_SPECS,
  noAction,
  Switch,
  SwitchRow,
} from "./messaging/components";
import m from "./messaging/messaging.module.css";
import styles from "./pages.module.css";

/** Banners carry a title, a short body and a button label: no long detail. */
const FIELDS: readonly ContentField[] = ["title", "body", "cta"];
const FIELD_SPECS: readonly FieldSpec<ContentField>[] = [
  { ...MESSAGE_FIELD_SPECS.title, placeholder: "e.g. Tournaments are coming" },
  { ...MESSAGE_FIELD_SPECS.body, placeholder: "e.g. Get notified about events and prizes." },
  { ...MESSAGE_FIELD_SPECS.cta, placeholder: "e.g. Turn on" },
];

export function BannersPage() {
  const canEdit = useCan("banners.edit");
  const { admin } = useAuth();
  const list = useBanners();
  const toggle = useUpdateBanner();
  const remove = useDeleteBanner();
  const [editing, setEditing] = useState<AdminBanner | "new" | null>(null);
  const [deleting, setDeleting] = useState<AdminBanner | null>(null);

  return (
    <>
      <PageHeader
        title="Banners"
        meta={
          canEdit ? (
            <span>Cards on Home, Arena and Profile. The app shows the live ones for each player's audience, highest priority first; every change is audited.</span>
          ) : (
            <span className={styles.readOnly} data-testid="read-only">
              Read-only · changing banners needs the editor role (you are {admin?.role})
            </span>
          )
        }
        actions={
          canEdit ? (
            <Button variant="primary" onClick={() => setEditing("new")} data-testid="new-banner">
              New banner
            </Button>
          ) : null
        }
      />
      <InlineError error={toggle.error} />
      {list.isPending ? (
        <Card>
          <Loading rows={6} />
        </Card>
      ) : list.isError ? (
        <ErrorState error={list.error} onRetry={() => void list.refetch()} />
      ) : (
        <div className={b.stack}>
          {BANNER_PLACEMENTS.map((p) => (
          <PlacementCard
            key={p}
            placement={p}
            items={list.data.items.filter((x) => x.placement === p)}
            canEdit={canEdit}
            busyId={toggle.isPending ? (toggle.variables?.id ?? null) : null}
            onToggle={(x) => toggle.mutate({ id: x.id, patch: { active: !x.active } })}
            onEdit={setEditing}
          />
          ))}
        </div>
      )}

      {editing ? (
        <BannerDialog
          banner={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onDelete={(x) => {
            setEditing(null);
            remove.reset();
            setDeleting(x);
          }}
        />
      ) : null}

      <ConfirmDialog
        open={deleting !== null}
        title={`Delete “${deleting?.content.en?.title ?? ""}”?`}
        confirmLabel="Delete banner"
        tone="danger"
        busy={remove.isPending}
        error={remove.error}
        onCancel={() => setDeleting(null)}
        onConfirm={() => deleting && remove.mutate(deleting.id, { onSuccess: () => setDeleting(null) })}
      >
        <p className={styles.prose}>It disappears from the app at once, and its stats go with it. To hide it for now and keep the numbers, pause it instead.</p>
      </ConfirmDialog>
    </>
  );
}

// ---------------------------------------------------------------- list

/** "4 Oct, 13:24" (UTC, as the column says). */
const when = (iso: string) => formatDateTime(iso).replace(/ UTC$/, "");

function actionText(x: Pick<AdminBanner, "action" | "link">): string {
  if (x.action === "link" && x.link) return `Opens ${APP_LINK_LABEL[x.link]}`;
  return ACTION_LABEL[x.action];
}

function PlacementCard({
  placement,
  items,
  canEdit,
  busyId,
  onToggle,
  onEdit,
}: {
  placement: BannerPlacement;
  items: AdminBanner[];
  canEdit: boolean;
  busyId: string | null;
  onToggle: (x: AdminBanner) => void;
  onEdit: (x: AdminBanner) => void;
}) {
  const live = items.filter((x) => x.state === "live").length;
  return (
    <Card
      title={PLACEMENT_LABEL[placement]}
      padded={false}
      actions={
        <Chip tone={live ? "success" : "muted"}>
          {live} live · {items.length} total
        </Chip>
      }
    >
      <div data-testid={`placement-${placement}`}>
        {items.length === 0 ? (
          <EmptyState title={`No banners on ${PLACEMENT_LABEL[placement]}`}>{canEdit ? "Create one with “New banner”." : "Editors create them here."}</EmptyState>
        ) : (
          <div className={tableStyles.wrap}>
            <table className={tableStyles.table}>
              <thead>
                <tr>
                  <th>Banner</th>
                  <th>State</th>
                  <th>Audience</th>
                  <th>Schedule (UTC)</th>
                  <th data-align="right">Priority</th>
                  <th>Players</th>
                  <th data-align="right">CTR</th>
                </tr>
              </thead>
              <tbody>
                {items.map((x) => (
                  <tr
                    key={x.id}
                    data-testid="banner-row"
                    data-state={x.state}
                    data-clickable={canEdit || undefined}
                    onClick={canEdit ? (e) => !(e.target as HTMLElement).closest("button") && onEdit(x) : undefined}
                  >
                    <td>
                      <div className={b.cell}>
                        <Thumb image={x.image} style={x.style} />
                        <div className={b.cellText}>
                          {canEdit ? (
                            <button type="button" className={b.title} onClick={() => onEdit(x)} aria-label={`Edit “${x.content.en?.title ?? ""}”`}>
                              {x.content.en?.title}
                            </button>
                          ) : (
                            <strong>{x.content.en?.title}</strong>
                          )}
                          <span>{x.content.en?.body}</span>
                          <span className={b.meta}>
                            {STYLE_LABEL[x.style]} · {actionText(x)} · {Object.keys(x.content).length} {Object.keys(x.content).length === 1 ? "language" : "languages"}
                            {x.dismissible ? "" : " · not dismissible"}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className={b.state}>
                        <Chip tone={STATE_TONE[x.state]}>{x.state}</Chip>
                        {canEdit ? (
                          <Switch checked={x.active} onChange={() => onToggle(x)} label={`${x.active ? "Pause" : "Resume"} “${x.content.en?.title ?? ""}”`} disabled={busyId === x.id} testId="banner-active" />
                        ) : null}
                      </div>
                    </td>
                    <td>
                      <span className={b.audience}>{BANNER_AUDIENCE_LABEL[x.audience]}</span>
                    </td>
                    <td className={b.schedule}>
                      <span>{x.endsAt ? when(x.startsAt) : `From ${when(x.startsAt)}`}</span>
                      <span>{x.endsAt ? `→ ${when(x.endsAt)}` : "no end"}</span>
                    </td>
                    <td data-align="right" className="mono">
                      {x.priority}
                    </td>
                    <td>
                      <dl className={b.stats}>
                        <div>
                          <dt>shown</dt>
                          <dd>{fmtInt(x.stats.shown)}</dd>
                        </div>
                        <div>
                          <dt>tapped</dt>
                          <dd>{fmtInt(x.stats.tapped)}</dd>
                        </div>
                        <div>
                          <dt>dismissed</dt>
                          <dd>{fmtInt(x.stats.dismissed)}</dd>
                        </div>
                      </dl>
                    </td>
                    <td data-align="right" className={b.ctr} data-testid="ctr">
                      {fmtPct(x.stats.ctr, 1)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Card>
  );
}

function Thumb({ image, style }: { image: ImageRef | null; style: BannerStyle }) {
  return image ? (
    <img src={image.url} alt="" className={b.thumb} data-style={style} data-testid="banner-thumb" />
  ) : (
    <span className={b.thumb} data-style={style} data-empty aria-hidden>
      no image
    </span>
  );
}

// ---------------------------------------------------------------- create / edit

const AUDIENCE_HINT: Record<BannerAudience, string> = {
  all: "Every player.",
  free: "Players without Pro (from their entitlement).",
  pro: "Players with Pro.",
  news_off: "Players who haven't turned on News & events: the audience for the opt-in card.",
};

function BannerDialog({ banner, onClose, onDelete }: { banner: AdminBanner | null; onClose: () => void; onDelete: (x: AdminBanner) => void }) {
  const create = useCreateBanner();
  const update = useUpdateBanner();
  const announcements = useBroadcasts();
  const [placement, setPlacement] = useState<BannerPlacement>(banner?.placement ?? "home");
  const [style, setStyle] = useState<BannerStyle>(banner?.style ?? "hero");
  const [content, setContent] = useState<ContentState>(() => (banner ? contentStateFrom(banner.content) : emptyContentState()));
  const [locale, setLocale] = useState<Locale>("en");
  const [image, setImage] = useState<ImageRef | null>(banner?.image ?? null);
  const [action, setAction] = useState<ActionValue>(() =>
    banner ? { action: banner.action, link: banner.link ?? "home", url: banner.url ?? "", announcementId: banner.announcementId } : noAction(),
  );
  const [audience, setAudience] = useState<BannerAudience>(banner?.audience ?? "all");
  const [startsAt, setStartsAt] = useState(() => toUtcInput(banner?.startsAt));
  const [endsAt, setEndsAt] = useState(() => toUtcInput(banner?.endsAt));
  const [priority, setPriority] = useState(String(banner?.priority ?? 0));
  const [dismissible, setDismissible] = useState(banner?.dismissible ?? true);
  const [active, setActive] = useState(banner?.active ?? true);
  const save = banner ? update : create;

  const { content: payload, incomplete } = toContent(content.draft, FIELDS);
  const en = content.draft.en;
  const priorityN = Number(priority);
  const startIso = fromUtcInput(startsAt);
  const endIso = fromUtcInput(endsAt);
  const problem =
    !en.title.trim() || !en.body.trim()
      ? "Write the English title and message."
      : action.action !== "none" && !en.cta.trim()
        ? "The button needs an English label."
        : (actionProblem(action) ??
          (incomplete.length
            ? `Finish or clear ${incomplete.map((l) => LOCALE_LABEL[l]).join(", ")}.`
            : !Number.isInteger(priorityN) || priorityN < PRIORITY_MIN || priorityN > PRIORITY_MAX
              ? `Priority: a whole number from ${PRIORITY_MIN} to ${PRIORITY_MAX}.`
              : endIso && Date.parse(endIso) <= Date.parse(startIso ?? new Date().toISOString())
                ? "The end must be after the start."
                : null));

  const submit = () => {
    const draft: BannerDraft = {
      placement,
      style,
      content: payload,
      imageId: image?.id ?? null,
      ...actionFields(action),
      audience,
      // Unchanged times keep their exact value; an emptied start means now (a new banner leaves it to the API).
      ...(banner && startsAt === toUtcInput(banner.startsAt) ? {} : startIso ? { startsAt: startIso } : banner ? { startsAt: new Date().toISOString() } : {}),
      endsAt: banner && endsAt === toUtcInput(banner.endsAt) ? banner.endsAt : endIso,
      priority: priorityN,
      active,
      dismissible,
    };
    const done = { onSuccess: onClose };
    if (banner) update.mutate({ id: banner.id, patch: draft }, done);
    else create.mutate(draft, done);
  };

  const recent = (announcements.data?.items ?? []).filter((x) => x.inbox).map((x) => ({ id: x.id, title: x.title, createdAt: x.createdAt }));
  return (
    <Dialog
      open
      title={banner ? "Edit banner" : "New banner"}
      onClose={onClose}
      width={1100}
      footer={
        <>
          <div className={b.footLeft}>
            {banner ? (
              <Button variant="ghost" onClick={() => onDelete(banner)} disabled={save.isPending}>
                Delete…
              </Button>
            ) : null}
            {problem ? (
              <span className={b.footProblem} data-testid="banner-problem">
                {problem}
              </span>
            ) : null}
          </div>
          <Button variant="ghost" onClick={onClose} disabled={save.isPending}>
            Cancel
          </Button>
          <Button variant="primary" busy={save.isPending} disabled={problem !== null} onClick={submit} data-testid="banner-save">
            {banner ? "Save changes" : "Create banner"}
          </Button>
        </>
      }
    >
      <div className={b.editor}>
        <div className={b.form}>
          <div className={b.twoUp}>
            <Field label="Placement">
              <Segmented label="Placement" value={placement} onChange={setPlacement} options={BANNER_PLACEMENTS.map((p) => ({ value: p, label: PLACEMENT_LABEL[p] }))} />
            </Field>
            <Field label="Style" hint={style === "hero" ? "Full-width image on top, then the text and a button." : "Square thumbnail on the left; the whole card is tappable."}>
              <Segmented label="Style" value={style} onChange={setStyle} options={BANNER_STYLES.map((s) => ({ value: s, label: STYLE_LABEL[s] }))} />
            </Field>
          </div>
          <ImageField image={image} onChange={setImage} recommended={BANNER_IMAGE[style]} hint={`recommended: ${BANNER_IMAGE[style].hint}`} square={style === "compact"} />
          <ContentEditor
            value={content}
            onChange={setContent}
            fields={FIELD_SPECS}
            required={MESSAGE_REQUIRED}
            locale={locale}
            onLocale={setLocale}
            needs={{ cta: action.action !== "none" ? "required for the button" : null }}
          />
          <ActionFields value={action} onChange={setAction} actions={["none", "link", "announcement", "news_optin", "url"]} announcements={recent} label="Tap action" />
          <div className={b.twoUp}>
            <Field label="Audience" hint={AUDIENCE_HINT[audience]}>
              <Select value={audience} onChange={(e) => setAudience(e.target.value as BannerAudience)} data-testid="banner-audience">
                {BANNER_AUDIENCES.map((a) => (
                  <option key={a} value={a}>
                    {BANNER_AUDIENCE_LABEL[a]}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Priority" hint="Higher shows first; ties: the newest.">
              <TextInput type="number" min={PRIORITY_MIN} max={PRIORITY_MAX} step={1} value={priority} onChange={(e) => setPriority(e.target.value)} data-testid="banner-priority" />
            </Field>
          </div>
          <div className={b.twoUp}>
            <Field label="Starts" hint="UTC. Empty: now.">
              <TextInput type="datetime-local" value={startsAt} onChange={(e) => setStartsAt(e.target.value)} data-testid="banner-starts" />
            </Field>
            <Field label="Ends" hint="UTC. Empty: no end.">
              <TextInput type="datetime-local" value={endsAt} onChange={(e) => setEndsAt(e.target.value)} data-testid="banner-ends" />
            </Field>
          </div>
          <div className={b.twoUp}>
            <SwitchRow title="Dismissible" hint="Players can close it for good (×)." checked={dismissible} onChange={setDismissible} label="Dismissible" testId="banner-dismissible" />
            <SwitchRow title="Active" hint="Off: paused, hidden from everyone until you turn it back on." checked={active} onChange={setActive} label="Active" testId="banner-active-field" />
          </div>
          <InlineError error={save.error} />
        </div>
        <aside className={b.previewSide}>
          <BannerPreview content={content} locale={locale} placement={placement} style={style} image={image} action={action} dismissible={dismissible} />
        </aside>
      </div>
    </Dialog>
  );
}

// ---------------------------------------------------------------- preview

function BannerPreview({
  content,
  locale,
  placement,
  style,
  image,
  action,
  dismissible,
}: {
  content: ContentState;
  locale: Locale;
  placement: BannerPlacement;
  style: BannerStyle;
  image: ImageRef | null;
  action: ActionValue;
  dismissible: boolean;
}) {
  const shown = resolveEntry(content.draft, locale);
  const card = { title: shown.title || "Title", body: shown.body || "Your message", cta: action.action === "none" ? null : shown.cta || "Button", image, dismissible };
  return (
    <div className={b.preview} data-testid="banner-preview">
      <div className={m.previewHead}>
        <span className="monoLabel">Preview · {LOCALE_LABEL[locale]}</span>
        {shown.fallback.length ? <span className={m.fallbackNote}>English for: {shown.fallback.join(", ")}</span> : null}
      </div>
      {BANNER_STYLES.map((s) => (
        <div key={s} className={b.slot} data-selected={s === style || undefined} data-testid={`preview-${s}`}>
          <span className={b.slotLabel}>
            {STYLE_LABEL[s]}
            {s === style ? " · selected" : ""}
          </span>
          <div className={m.phone} dir={RTL_LOCALES.has(locale) ? "rtl" : undefined}>
            <div className={m.phoneBar}>
              <span>{PLACEMENT_LABEL[placement]}</span>
              <span>9:41</span>
            </div>
            <BannerCard style={s} {...card} />
          </div>
        </div>
      ))}
    </div>
  );
}

/** The card as the app draws it: hero (image on top, text, button) or compact (thumbnail, text, the card is the tap). */
export function BannerCard({ style, title, body, cta, image, dismissible }: { style: BannerStyle; title: string; body: string; cta: string | null; image: ImageRef | null; dismissible: boolean }) {
  const close = dismissible ? (
    <span className={b.close} aria-label="Dismiss">
      ×
    </span>
  ) : null;
  if (style === "compact") {
    return (
      <div className={b.card} data-style="compact">
        {close}
        {image ? <img src={image.url} alt="" className={b.compactImage} /> : <span className={b.compactImage} data-empty aria-hidden />}
        <div className={b.cardText}>
          <strong>{title}</strong>
          <p>{body}</p>
          {cta ? <span className={b.compactCta}>{cta} ›</span> : null}
        </div>
      </div>
    );
  }
  return (
    <div className={b.card} data-style="hero">
      {close}
      {image ? <img src={image.url} alt="" className={b.heroImage} /> : null}
      <div className={b.cardText}>
        <strong>{title}</strong>
        <p>{body}</p>
      </div>
      {cta ? <span className={b.heroCta}>{cta}</span> : null}
    </div>
  );
}
